import React, { lazy, Suspense, useState, useMemo, useEffect } from 'react';
import { useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Card, CardContent,
  List, ListItem, ListItemIcon, ListItemText, alpha, useTheme, useMediaQuery,
  LinearProgress, Alert, CircularProgress, Snackbar, Tooltip, Divider,
  FormControlLabel, Switch, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Folder as FolderIcon, Description as DescriptionIcon, Image as ImageIcon,
  PictureAsPdf as PictureAsPdfIcon, InsertDriveFile as InsertDriveFileIcon,
  CloudUpload as CloudUploadIcon, Download as DownloadIcon, Delete as DeleteIcon,
  Share as ShareIcon, Visibility as VisibilityIcon, Search as SearchIcon,
  Article as ArticleIcon, Refresh as RefreshIcon, Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon, Info as InfoIcon
} from '@mui/icons-material';

import documentService from '../services/documentService';
import projectService from '../services/projectService';
import { downloadBlobFile, isNativeMobile, openBlobFile } from '../services/nativeFileService';
import { usePermissions } from '../context/PermissionsContext';

const SalesFilesPanel = lazy(() => import('../components/SalesFilesPanel'));

const categories = ['All', 'Contracts', 'Proposals', 'Designs', 'Documents', 'Legal', 'Fiscal'];
const DOCUMENT_LIBRARY_VIEWS = {
  all: 'all',
  general: 'general',
  sales: 'sales'
};

const getFileIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'pdf': return <PictureAsPdfIcon sx={{ color: '#d32f2f' }} />;
    case 'doc': case 'docx': return <DescriptionIcon sx={{ color: '#1976d2' }} />;
    case 'jpg': case 'jpeg': case 'png': case 'gif': return <ImageIcon sx={{ color: '#2e7d32' }} />;
    case 'xls': case 'xlsx': return <ArticleIcon sx={{ color: '#2e7d32' }} />;
    default: return <InsertDriveFileIcon sx={{ color: '#757575' }} />;
  }
};

export default function DocumentManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';
  const { hasPermission } = usePermissions();
  const canViewSalesFiles = hasPermission('billingDocuments', 'view');

  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [libraryView, setLibraryView] = useState(DOCUMENT_LIBRARY_VIEWS.all);
  
  const [openUpload, setOpenUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // ✅ ESTADOS PARA VISTA PREVIA
  const [viewDialog, setViewDialog] = useState({ open: false, doc: null });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [shareDialog, setShareDialog] = useState({ open: false, doc: null, shareLink: '' });

  const [newDocument, setNewDocument] = useState({
    file: null, project_name: '', category: 'Documents', description: '', shared: false,
  });

  const [stats, setStats] = useState({ totalFiles: 0, sharedFiles: 0, projects: 0 });
  const token = localStorage.getItem('token');

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      const effectiveProject = libraryView === DOCUMENT_LIBRARY_VIEWS.general
        ? 'General'
        : selectedProject;
      if (effectiveProject) params.project = effectiveProject;
      if (selectedCategory !== 'All') params.category = selectedCategory;
      const data = await documentService.getDocuments(token, params);
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Error loading docs:', err);
      setError('Error loading documents');
    } finally {
      setLoading(false);
    }
  }, [libraryView, selectedCategory, selectedProject, token]);

  const loadStats = useCallback(async () => {
    try {
      const data = await documentService.getStats(token);
      setStats(data.stats || { totalFiles: 0, sharedFiles: 0, projects: 0 });
    } catch (err) { console.error('Error stats:', err); }
  }, [token]);

  const loadProjects = useCallback(async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(data.projects || (Array.isArray(data) ? data : []) || []);
    } catch (err) { console.error('Error projects:', err); }
  }, []);

  useEffect(() => {
    if (!token) return;
    loadStats();
    loadProjects();
  }, [token, loadStats, loadProjects]);

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      const matchProject = libraryView === DOCUMENT_LIBRARY_VIEWS.general
        ? doc.project_name === 'General'
        : (selectedProject ? doc.project_name === selectedProject : true);
      const matchCategory = selectedCategory !== 'All' ? doc.category === selectedCategory : true;
      const matchSearch = searchQuery ? doc.original_name?.toLowerCase().includes(searchQuery.toLowerCase()) : true;
      return matchProject && matchCategory && matchSearch;
    });
  }, [documents, selectedProject, selectedCategory, searchQuery, libraryView]);

  useEffect(() => {
    if (token) loadDocuments();
  }, [token, loadDocuments]);

  const internalFilesCount = useMemo(
    () => documents.filter((doc) => doc.project_name === 'General').length,
    [documents]
  );

  const projectLinkedFilesCount = useMemo(
    () => documents.filter((doc) => doc.project_name && doc.project_name !== 'General').length,
    [documents]
  );

  const handleRefresh = useCallback(() => {
    loadDocuments();
    loadStats();
    loadProjects();
  }, [loadDocuments, loadProjects, loadStats]);

  // ============ VISTA PREVIA ============
  const handleView = async (doc) => {
    if (isNativeMobile()) {
      try {
        setLoadingPreview(true);
        const blob = await documentService.downloadDocument(doc.id, token);
        await openBlobFile(blob, {
          fileName: doc.original_name || `document-${doc.id}`,
          mimeType: blob.type
        });
      } catch (error) {
        console.error('Error opening native preview:', error);
        setSnackbar({ open: true, message: 'Could not open this document', severity: 'error' });
      } finally {
        setLoadingPreview(false);
      }
      return;
    }

    setViewDialog({ open: true, doc });
    setPreviewUrl(null); 
    
    const fileType = doc.file_type?.toLowerCase();
    const isPreviewable = /pdf|jpg|jpeg|png|gif/.test(fileType);

    if (isPreviewable) {
        setLoadingPreview(true);
        try {
            const blob = await documentService.downloadDocument(doc.id, token);
            const url = window.URL.createObjectURL(blob);
            setPreviewUrl(url);
        } catch (error) {
            console.error("Error preview:", error);
            setSnackbar({ open: true, message: 'Could not load preview', severity: 'error' });
        } finally {
            setLoadingPreview(false);
        }
    }
  };

  const handleCloseView = () => {
    setViewDialog({ open: false, doc: null });
    if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
    }
  };

  // ============ ACCIONES ============
  const handleDownload = async (doc) => {
    try {
      setSnackbar({ open: true, message: 'Downloading...', severity: 'info' });
      const blob = await documentService.downloadDocument(doc.id, token);
      await downloadBlobFile(blob, {
        fileName: doc.original_name || `document-${doc.id}`,
        mimeType: blob.type
      });
      setSnackbar({ open: true, message: `Downloaded: ${doc.original_name}`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await documentService.deleteDocument(id, token);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      setSnackbar({ open: true, message: 'Deleted successfully', severity: 'success' });
      loadStats();
    } catch (err) { setSnackbar({ open: true, message: err.message, severity: 'error' }); }
  };

  const handleShare = async (doc) => {
    try {
        const shareLink = `${window.location.origin}/documents/share/${doc.id}`;
        if (navigator.share) {
            await navigator.share({ title: 'Shared Document', text: doc.original_name, url: shareLink });
        } else {
            setShareDialog({ open: true, doc, shareLink });
        }
    } catch (err) { console.error(err); }
  };

  const handleCopyShareLink = async () => {
    await navigator.clipboard.writeText(shareDialog.shareLink);
    setSnackbar({ open: true, message: 'Copied to clipboard', severity: 'success' });
    setShareDialog({ open: false, doc: null, shareLink: '' });
  };

  const handleUpload = async () => {
    if (!newDocument.file) return setSnackbar({ open: true, message: 'Select a file', severity: 'error' });
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', newDocument.file);
      formData.append('project_name', newDocument.project_name || 'General');
      formData.append('category', newDocument.category);
      formData.append('description', newDocument.description);
      formData.append('shared', newDocument.shared ? '1' : '0');

      const interval = setInterval(() => setUploadProgress((p) => (p >= 90 ? 90 : p + 10)), 200);
      const data = await documentService.uploadDocument(formData, token);
      clearInterval(interval);
      setUploadProgress(100);
      setSnackbar({ open: true, message: data.message, severity: 'success' });
      setOpenUpload(false);
      setNewDocument({ file: null, project_name: '', category: 'Documents', description: '', shared: false });
      loadDocuments();
      loadStats();
    } catch (err) { setSnackbar({ open: true, message: err.message, severity: 'error' }); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  if (libraryView === DOCUMENT_LIBRARY_VIEWS.sales && canViewSalesFiles) {
    return (
      <Box sx={{ p: 0 }}>
        <Paper elevation={isDarkMode ? 3 : 0} sx={{ p: { xs: 1.4, md: 1.7 }, mb: 1.5, borderRadius: 2.5, background: 'linear-gradient(135deg, #0f4c5c 0%, #168aad 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 2, p: 1 }}><FolderIcon sx={{ fontSize: 24 }} /></Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Document Management</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Commercial dossiers connecting Sales Docs, customer orders, invoices, and payment evidence.</Typography>
            </Box>
          </Box>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.2, mb: 2.5, borderRadius: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip clickable label="All Uploaded Files" variant="outlined" onClick={() => setLibraryView(DOCUMENT_LIBRARY_VIEWS.all)} />
            <Chip clickable label="General / Internal" variant="outlined" onClick={() => setLibraryView(DOCUMENT_LIBRARY_VIEWS.general)} />
            <Chip clickable label="Sales Files" color="info" onClick={() => setLibraryView(DOCUMENT_LIBRARY_VIEWS.sales)} />
          </Box>
        </Paper>
        <Suspense fallback={<Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>}>
          <SalesFilesPanel />
        </Suspense>
      </Box>
    );
  }

  if (loading && documents.length === 0) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 0 }}>
      <Paper elevation={isDarkMode ? 3 : 0} sx={{ p: { xs: 1.4, md: 1.7 }, mb: 1.5, borderRadius: 2.5, background: 'linear-gradient(135deg, #135cb4 0%, #102f66 100%)', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1 }}><FolderIcon sx={{ fontSize: 24 }} /></Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Document Management</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Only uploaded files live here. Quotes, invoices and POs stay in their own modules.</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr)', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Tooltip title="Refresh"><IconButton aria-label="Refresh document library" onClick={handleRefresh} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}><RefreshIcon /></IconButton></Tooltip>
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setOpenUpload(true)} size={isMobile ? 'small' : 'medium'} sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}>{isMobile ? 'Upload' : 'Upload File'}</Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 1.2, '&:last-child': { pb: 1.2 } }}>
                    <Typography variant="body2" color="text.secondary">Total Files</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1976d2' }}>{stats.totalFiles}</Typography>
                </CardContent>
            </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 1.2, '&:last-child': { pb: 1.2 } }}>
                    <Typography variant="body2" color="text.secondary">Shared</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#ed6c02' }}>{stats.sharedFiles}</Typography>
                </CardContent>
            </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 1.2, '&:last-child': { pb: 1.2 } }}>
                    <Typography variant="body2" color="text.secondary">General / Internal</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#7c3aed' }}>{internalFilesCount}</Typography>
                </CardContent>
            </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <CardContent sx={{ textAlign: 'center', py: 1.2, '&:last-child': { pb: 1.2 } }}>
                    <Typography variant="body2" color="text.secondary">Project-linked</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#9c27b0' }}>{projectLinkedFilesCount}</Typography>
                </CardContent>
            </Card>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 1.4, mb: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            clickable
            label="All Uploaded Files"
            color={libraryView === DOCUMENT_LIBRARY_VIEWS.all ? 'primary' : 'default'}
            variant={libraryView === DOCUMENT_LIBRARY_VIEWS.all ? 'filled' : 'outlined'}
            onClick={() => setLibraryView(DOCUMENT_LIBRARY_VIEWS.all)}
          />
          <Chip
            clickable
            label="General / Internal"
            color={libraryView === DOCUMENT_LIBRARY_VIEWS.general ? 'primary' : 'default'}
            variant={libraryView === DOCUMENT_LIBRARY_VIEWS.general ? 'filled' : 'outlined'}
            onClick={() => setLibraryView(DOCUMENT_LIBRARY_VIEWS.general)}
          />
          {canViewSalesFiles && (
            <Chip
              clickable
              label="Sales Files"
              color="info"
              variant="outlined"
              onClick={() => setLibraryView(DOCUMENT_LIBRARY_VIEWS.sales)}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {libraryView === DOCUMENT_LIBRARY_VIEWS.general
            ? 'Showing only internal files not linked to any project.'
            : 'Showing all uploaded files. Use project and category filters to narrow the list.'}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(220px, 1fr) minmax(180px, .55fr)' }, gap: 1.25, alignItems: 'center' }}>
          <TextField size="small" placeholder="Search uploaded files..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ minWidth: 0 }} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }} />
          <TextField size="small" select label="Project" value={libraryView === DOCUMENT_LIBRARY_VIEWS.general ? 'General' : selectedProject} onChange={(e) => setSelectedProject(e.target.value)} sx={{ minWidth: 0 }} disabled={libraryView === DOCUMENT_LIBRARY_VIEWS.general}>
            <MenuItem value="">All projects</MenuItem>
            <MenuItem value="General"><em>General / Internal</em></MenuItem>
            {projects.map((p) => (<MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>))}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {categories.map((cat) => (
            <Chip key={cat} label={cat} onClick={() => setSelectedCategory(cat)} color={selectedCategory === cat ? 'primary' : 'default'} variant={selectedCategory === cat ? 'filled' : 'outlined'} />
          ))}
          <Chip label={`${filteredDocuments.length} file${filteredDocuments.length === 1 ? '' : 's'}`} color="primary" variant="outlined" />
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <List sx={{ p: 0 }}>
          {filteredDocuments.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}><FolderIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} /><Typography color="text.secondary">No files found</Typography></Box>
          ) : (
            filteredDocuments.map((doc, idx) => (
              <React.Fragment key={doc.id}>
                <ListItem sx={{ py: 2, display: 'grid', gridTemplateColumns: { xs: '32px minmax(0, 1fr)', sm: '32px minmax(0, 1fr) auto' }, alignItems: 'center', columnGap: 1, rowGap: 1, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}>
                  <ListItemIcon sx={{ minWidth: 0 }}>{getFileIcon(doc.file_type)}</ListItemIcon>
                  <ListItemText
                    primary={<Box sx={{ display: 'flex', minWidth: 0, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}><Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{doc.original_name}</Typography>{doc.shared && <Chip label="Shared" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />}</Box>}
                    secondary={
                        <Box sx={{ mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {doc.project_name === 'General' ? <Chip icon={<BusinessIcon style={{fontSize: 14}} />} label="General" size="small" variant="outlined" sx={{height: 20, fontSize: '0.7rem'}} /> : <Typography variant="caption" fontWeight={600}>{doc.project_name}</Typography>}
                                <Typography variant="caption" color="text.secondary">• {doc.category} • {doc.file_size}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">Uploaded by {doc.uploaded_by_name} on {new Date(doc.created_at).toLocaleDateString()}</Typography>
                        </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', gridColumn: { xs: '1 / -1', sm: 'auto' }, alignSelf: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
                    <Tooltip title="View"><IconButton aria-label={`View ${doc.original_name}`} onClick={() => handleView(doc)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Download"><IconButton aria-label={`Download ${doc.original_name}`} onClick={() => handleDownload(doc)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Share"><IconButton aria-label={`Share ${doc.original_name}`} onClick={() => handleShare(doc)}><ShareIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton aria-label={`Delete ${doc.original_name}`} color="error" onClick={() => handleDelete(doc.id, doc.original_name)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Box>
                </ListItem>
                {idx < filteredDocuments.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>

      {/* ✅ DIÁLOGO DE VISTA PREVIA (Combina Archivo + Detalles) */}
      <Dialog open={viewDialog.open} onClose={handleCloseView} maxWidth="lg" fullWidth fullScreen={isMobile}>
        {viewDialog.doc && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'hidden' }}>
                    {getFileIcon(viewDialog.doc.file_type)}
                    <Typography variant="h6" noWrap>{viewDialog.doc.original_name}</Typography>
                </Box>
                <IconButton aria-label="Close document preview" onClick={handleCloseView}>×</IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', height: '80vh' }}>
                {/* 1. SECCIÓN DE PREVISUALIZACIÓN */}
                <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
                    {loadingPreview ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}><CircularProgress /> <Typography sx={{ ml: 2 }}>Loading file...</Typography></Box>
                    ) : previewUrl ? (
                        viewDialog.doc.file_type === 'pdf' ? (
                            <iframe src={previewUrl} width="100%" height="100%" style={{ border: 'none' }} title="PDF Preview" />
                        ) : (
                            <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        )
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <InsertDriveFileIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>Preview not available</Typography>
                            <Typography color="text.secondary">Use the Download button to view this file.</Typography>
                        </Box>
                    )}
                </Box>

                {/* 2. SECCIÓN DE DETALLES (RESTAURADA) - Usa un Acordeón para no ocupar mucho espacio en móvil */}
                <Accordion defaultExpanded elevation={3} sx={{ flexShrink: 0 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoIcon color="action" fontSize="small" />
                            <Typography variant="subtitle2" fontWeight={600}>File Details</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Project</Typography>
                                <Typography variant="body2">{viewDialog.doc.project_name}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Category</Typography>
                                <Typography variant="body2">{viewDialog.doc.category}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Uploaded By</Typography>
                                <Typography variant="body2">{viewDialog.doc.uploaded_by_name}</Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Date</Typography>
                                <Typography variant="body2">{new Date(viewDialog.doc.created_at).toLocaleDateString()}</Typography>
                            </Grid>
                            {viewDialog.doc.description && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Description</Typography>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{viewDialog.doc.description}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </DialogContent>
            
            <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', p: 2 }}>
                <Button onClick={handleCloseView}>Close</Button>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => handleDownload(viewDialog.doc)}>Download File</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={openUpload} onClose={() => !uploading && setOpenUpload(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Upload File</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button variant="outlined" fullWidth component="label" startIcon={<CloudUploadIcon />} sx={{ py: 3, borderStyle: 'dashed' }} disabled={uploading}>
                {newDocument.file ? newDocument.file.name : 'Select File'}
                <input type="file" hidden onChange={(e) => setNewDocument({ ...newDocument, file: e.target.files[0] })} />
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select label="Project (Optional)" value={newDocument.project_name} onChange={(e) => setNewDocument({ ...newDocument, project_name: e.target.value })} disabled={uploading}>
                <MenuItem value=""><em>General / Internal (No Project)</em></MenuItem>
                {projects.map((p) => (<MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select label="Category" value={newDocument.category} onChange={(e) => setNewDocument({ ...newDocument, category: e.target.value })} disabled={uploading}>
                {categories.filter(c => c !== 'All').map(c => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Description" value={newDocument.description} onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })} disabled={uploading} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={newDocument.shared} onChange={(e) => setNewDocument({ ...newDocument, shared: e.target.checked })} disabled={uploading} />} label="Share with team" /></Grid>
            {uploading && <Grid item xs={12}><LinearProgress variant="determinate" value={uploadProgress} /></Grid>}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUpload(false)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!newDocument.file || uploading}>Upload</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shareDialog.open} onClose={() => setShareDialog({ open: false, doc: null, shareLink: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Share</DialogTitle>
        <DialogContent dividers><TextField fullWidth value={shareDialog.shareLink} readOnly /></DialogContent>
        <DialogActions><Button onClick={() => setShareDialog({ open: false, doc: null, shareLink: '' })}>Cancel</Button><Button variant="contained" onClick={handleCopyShareLink}>Copy Link</Button></DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}><Alert severity={snackbar.severity}>{snackbar.message}</Alert></Snackbar>
    </Box>
  );
}
